import { type ServiceNameType } from '@consigli/types';

import { type AgentCategory, type AgentTag } from '@/util/service-config';

/** One tag as the card and the dropdown need it: the key to match, the label to read. */
export interface AgentTagOption {
  key: AgentTag;
  label: string;
}

/**
 * One agent as the directory renders it — a `serviceConfig` entry with its keys
 * already translated.
 *
 * Deliberately not `ServiceData` from `use-project-services.ts`: that shape
 * carries where a service is reached and whether this project has it applied,
 * and the directory is global and its cards are not links, so every one of those
 * fields would be dead weight here.
 */
export interface DirectoryAgent {
  serviceName: ServiceNameType;
  title: string;
  description: string;
  /** One or more, in `AGENT_CATEGORY` order. */
  categories: readonly AgentCategory[];
  tags: AgentTagOption[];
}

/**
 * "No filter", for both facets. A real value rather than `undefined`, because it
 * is also the value of the "All" pill and the "All tags" item — the controls need
 * something to render as selected.
 */
export const ALL_FILTER = 'all';

export type AgentCategoryFilter = AgentCategory | typeof ALL_FILTER;

export interface AgentFilters {
  category: AgentCategoryFilter;
  /** Empty is no filter; the category facet spells that as ALL_FILTER instead. */
  tags: readonly AgentTag[];
  searchText: string;
}

/**
 * Case- and whitespace-insensitive, so a query pasted with a double space or a
 * trailing newline still matches. Applied to both sides: normalising only the
 * query would leave "Site  Due Diligence" unmatchable if the copy ever carried
 * one.
 */
const normalise = (text: string): string => text.toLowerCase().replace(/\s+/g, ' ').trim();

/**
 * The three facets, AND-composed, over the already-visible agents. Tags are the
 * exception within their own facet, where several selections are ORed.
 *
 * Pure and synchronous: 28 entries, so there is nothing to debounce and no
 * reason for this to know about React. The search covers the title, the
 * description and the tag labels — the visible text of a card, minus the mark —
 * so typing "bids" finds the agents whose pill says it as well as the ones whose
 * name does.
 */
export const filterAgents = (agents: DirectoryAgent[], filters: AgentFilters): DirectoryAgent[] => {
  const query = normalise(filters.searchText);

  return agents.filter((agent) => {
    // An agent in several categories shows under each of them.
    if (filters.category !== ALL_FILTER && !agent.categories.includes(filters.category)) {
      return false;
    }

    // Any, not all. Tags are broad labels and an agent carries one to four of
    // them, so requiring every selected tag would answer most pairs with an
    // empty grid — "Bids, Risk" reads as widening the net, not narrowing it.
    // Facets still AND with each other: a category and two tags means that
    // category, carrying either tag.
    if (filters.tags.length > 0 && !agent.tags.some(({ key }) => filters.tags.includes(key))) {
      return false;
    }

    if (query === '') return true;

    return [agent.title, agent.description, ...agent.tags.map(({ label }) => label)].some((field) =>
      normalise(field).includes(query),
    );
  });
};
